<template>
  <div class="changeset-diff">
    <div v-for="entry in attributeEntries" :key="entry.attribute" class="changeset-diff__attribute-row">
      <span v-if="showAttributeLabels" class="changeset-diff__attribute">{{ attributeLabel(entry.attribute) }}</span>
      <template v-for="(value, index) in entry.values" :key="`${index}-${value.state}-${value.name}`">
        <span v-if="value.state === 'changed'" class="changeset-diff__value --changed">
          <template v-if="value.label">{{ value.label }} (</template>
          <template v-for="(detail, detailIndex) in value.details" :key="`${detailIndex}-${detail.key}`">
            <span class="changeset-diff__key">{{ detail.key }}: </span>
            <span
              v-for="(detailValue, detailValueIndex) in detail.values"
              :key="`${detailValueIndex}-${detailValue.state}-${detailValue.name}`"
              class="changeset-diff__value"
              :class="`--${detailValue.state}`"
            >
              <template v-if="detailValue.state === 'removed'">
                <span class="changeset-diff__sr-only">{{ removedLabel(detailValue.name) }}</span>
                <span aria-hidden="true">{{ detailValue.label }}</span>
              </template>
              <template v-else>{{ detailValue.label }}</template>
            </span>
          </template>
          <template v-if="value.label">)</template>
        </span>
        <span v-else class="changeset-diff__value" :class="`--${value.state}`">
          <template v-if="value.state === 'removed'">
            <!-- Strikethrough alone is silent for screen readers: expose the removal as
                 visually-hidden text and hide the styled copy. -->
            <span class="changeset-diff__sr-only">{{ removedLabel(value.name) }}</span>
            <span aria-hidden="true">{{ value.label }}</span>
          </template>
          <template v-else>{{ value.label }}</template>
        </span>
      </template>
    </div>
  </div>
</template>

<script setup>
/**
 * Standard changeset rendering for SpAdmin review tables — the frontend counterpart of
 * ChangeSet::Renderer's :diff style. Takes a `{ attribute: [old, new] }` changeset
 * and renders each changed attribute as kept / added (+ prefix, success colour) /
 * removed (struck through, error colour) values; list items whose payload changed keep
 * their label and show the per-key payload diff beside it.
 */
import { computed } from "vue";
import { changesetAttributeEntries } from "#ce/apps/SpAdmin/shared/utils/changeset-diff";

const props = defineProps({
  /** The changeset: attribute name to an [old, new] transition (scalars, arrays, or objects). */
  changeset: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Copy overrides: `attributes` maps attribute names to display labels;
   * `removedValue` is the screen-reader template for removed entries
   * ("Removed: %{name}").
   */
  labels: {
    type: Object,
    default: () => ({}),
  },
  /** Hide the per-attribute label when the surrounding table column already names it. */
  showAttributeLabels: {
    type: Boolean,
    default: true,
  },
});

const attributeEntries = computed(() => changesetAttributeEntries(props.changeset));

function attributeLabel(attribute) {
  return props.labels.attributes?.[attribute] ?? attribute;
}

function removedLabel(name) {
  return String(props.labels.removedValue ?? "Removed: %{name}").replaceAll("%{name}", name);
}
</script>

<style scoped lang="scss">
.changeset-diff {
  &__attribute {
    font-weight: var(--sp-ref-font-weight-bold, 700);

    &::after {
      content: ": ";
    }
  }

  &__key {
    color: var(--sp-sys-color-on-surface-variant, #4a4a4a);
  }

  &__value {
    display: inline-block;

    &:not(:last-child) {
      margin-inline-end: var(--sp-ref-scale-6);
    }

    // Component tokens rather than --sp-sys-color-success/error: the system palette's
    // success (#28a745) is below WCAG AA contrast for normal text on a light surface,
    // so these default to AA-verified shades while staying themable.
    // The "+" prefix carries the added meaning without colour.
    &.--added {
      color: var(--sp-comp-changeset-diff-added-color, #2e7d32);
    }

    // Strikethrough carries the removal without relying on colour alone.
    &.--removed {
      color: var(--sp-comp-changeset-diff-removed-color, #d32f2f);
      text-decoration: line-through;
    }
  }

  // Screen-reader-only text for the removed-value announcement.
  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
